import {z} from 'zod';

// ============================================================================
// SHARED VALUE SCHEMAS
// ============================================================================

/** Range value used by both filter types */
export const RangeValueSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
});
export type RangeValue = z.infer<typeof RangeValueSchema>;

// ============================================================================
// SHARED RANGE FIELDS
// ============================================================================

/** Range fields supported by both filter types */
export const SharedRangeFields = [
  'employees',
  'funding',
  'trafficRank',
] as const;
export type SharedRangeField = (typeof SharedRangeFields)[number];

// ============================================================================
// SCHEMA FACTORIES
// ============================================================================

/**
 * Create a filter item schema from a condition schema.
 * Produces the { type: 'group' | 'condition', ... } discriminated union.
 */
export function createFilterItemSchema<TCondition extends z.ZodTypeAny>(
  conditionSchema: TCondition
) {
  return z.discriminatedUnion('type', [
    z.object({
      type: z.literal('group'),
      operator: z.enum(['and', 'or']),
      conditions: z.array(conditionSchema),
    }),
    z.object({
      type: z.literal('condition'),
      condition: conditionSchema,
    }),
  ]);
}

/**
 * Create the conditions wrapper schema.
 * Produces { operator: 'and' | 'or', items: FilterItem[] }
 */
export function createFilterConditionsSchema<TCondition extends z.ZodTypeAny>(
  conditionSchema: TCondition
) {
  const itemSchema = createFilterItemSchema(conditionSchema);
  return z.object({
    operator: z.enum(['and', 'or']),
    items: z.array(itemSchema),
  });
}
